<template>
  <v-dialog :value="value" max-width="500" @input="onDialogInput">
    <div class="aw-list-popup">
      <strong class="aw-list-popup__title">출금 내역 조회</strong>

      <div v-if="loading" class="aw-list-popup__loading">
        <v-progress-circular indeterminate color="#ef761d" size="28" />
        <p>출금 내역을 불러오는 중입니다...</p>
      </div>

      <div v-else-if="!items || items.length === 0" class="aw-list-popup__empty">
        <p>조회된 출금 내역이 없습니다.</p>
      </div>

      <div v-else class="aw-list-popup__list">
        <div
          class="aw-item"
          v-for="(item, i) in items"
          :key="itemKey(item, i)"
          ref="awItems"
        >
          <!-- 헤더: 항상 노출, 클릭 시 펼치기/접기 -->
          <button type="button" class="aw-item__header" @click="toggleExpand(i)">
            <div class="aw-item__header-main">
              <span class="aw-item__date">{{ formatDate(item.send_date) }}</span>
            </div>
            <div class="aw-item__header-sub">
              <span>출금금액 <strong>{{ money(item.amount) }}</strong></span>
            </div>
            <span class="aw-item__chevron" :class="{ 'is-open': expandedIndex === i }">▾</span>
          </button>

          <!-- 상세: 펼쳤을 때만 노출 -->
          <div class="aw-item__detail" v-if="expandedIndex === i">
            <div class="aw-item__detail-row">
              <span class="aw-item__detail-label">출금(예정)일자</span>
              <span class="aw-item__detail-value">{{ formatDate(item.send_date) }}</span>
            </div>
            <div class="aw-item__detail-row">
              <span class="aw-item__detail-label">출금금액</span>
              <span class="aw-item__detail-value">{{ money(item.amount) }}</span>
            </div>
            <div class="aw-item__detail-row">
              <span class="aw-item__detail-label">계약자명</span>
              <span class="aw-item__detail-value">{{ dash(item.contractor_name) }}</span>
            </div>
            <div class="aw-item__detail-row">
              <span class="aw-item__detail-label">출금은행</span>
              <span class="aw-item__detail-value">{{ item.bank_name }}</span>
            </div>
            <div class="aw-item__detail-row">
              <span class="aw-item__detail-label">출금계좌</span>
              <span class="aw-item__detail-value">{{ item.account_number }}</span>
            </div>
            <div class="aw-item__detail-row">
              <span class="aw-item__detail-label">출금상태</span>
              <span class="aw-item__detail-value">{{ item.status }}</span>
            </div>
            <div class="aw-item__detail-row" v-if="item.fee">
              <span class="aw-item__detail-label">수수료</span>
              <span class="aw-item__detail-value is-amount">{{ money(item.fee) }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="aw-list-popup__footer">
        <button
          type="button"
          class="aw-list-popup__close"
          @click="$emit('input', false)"
        >
          닫기
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { comma } from "@/utils/filter.js";

export default {
  name: "AutoWithdrawListDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expandedIndex: null,
    };
  },
  watch: {
    value(val) {
      if (!val) this.expandedIndex = null;
    },
  },
  methods: {
    onDialogInput(val) {
      this.$emit("input", val);
    },
    toggleExpand(i) {
      this.expandedIndex = this.expandedIndex === i ? null : i;

      if (this.expandedIndex === null) return;

      // 펼친 항목이 팝업 하단에 가려지지 않도록 스크롤
      this.$nextTick(() => {
        const el = this.$refs.awItems && this.$refs.awItems[i];
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });
    },
    dash(val) {
      return val === null || val === undefined || val === "" ? "-" : val;
    },
    money(val) {
      if (val === null || val === undefined || val === "") return "-";
      return `${comma(val)}원`;
    },
    formatDate(dt) {
      if (!dt || dt.length !== 8) return dt || "-";
      return `${dt.slice(0, 4)}-${dt.slice(4, 6)}-${dt.slice(6, 8)}`;
    },
    itemKey(item, i) {
      return `${item.message_number || "history"}-${i}`;
    },
  },
};
</script>

<style scoped>
.aw-list-popup {
  border-radius: 20px;
  background-color: #ffffff;
  padding: 28px 24px 22px;
}

.aw-list-popup__title {
  display: block;
  margin-bottom: 24px;
  text-align: center;
  color: #e66a1f;
  font-size: 24px;
  font-weight: 700;
}

.aw-list-popup__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 0;
}

.aw-list-popup__loading p {
  margin: 0;
  color: #444444;
  font-size: 14px;
}

.aw-list-popup__empty {
  padding: 32px 0;
  text-align: center;
  color: #7d7d7d;
  font-size: 14px;
}

/* 팝업(.v-dialog)이 max-height 90%로 이미 스크롤되므로
   리스트에 별도 스크롤 영역을 두지 않는다 (상세 펼침 시 잘림 방지) */
.aw-list-popup__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

/* 아이템 카드 */
.aw-item {
  border: 1px solid #252525;
  border-radius: 12px;
  overflow: hidden;
  background-color: #ffffff;
}

/* 헤더 버튼 */
.aw-item__header {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  padding: 14px 16px;
  border: 0;
  background-color: #ffffff;
  text-align: left;
  cursor: pointer;
}

.aw-item__header-main {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 24px;
}

.aw-item__date {
  flex: 1 1 auto;
  color: #232323;
  font-size: 14px;
  font-weight: 700;
}

.aw-item__header-sub {
  display: flex;
  gap: 14px;
  color: #8c8c8c;
  font-size: 12px;
}

.aw-item__header-sub strong {
  color: #232323;
  font-weight: 700;
}

.aw-item__chevron {
  position: absolute;
  right: 14px;
  top: 14px;
  display: inline-block;
  color: #ef761d;
  font-size: 16px;
  line-height: 1;
  transition: transform 0.2s;
}

.aw-item__chevron.is-open {
  transform: rotate(180deg);
}

/* 상세 영역 */
.aw-item__detail {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px 16px;
  border-top: 1px solid #e5e5e5;
}

.aw-item__detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.aw-item__detail-label {
  flex: 0 0 90px;
  color: #8c8c8c;
  font-size: 13px;
}

.aw-item__detail-value {
  flex: 1 1 auto;
  color: #232323;
  font-size: 13px;
  text-align: right;
  word-break: break-all;
}

.aw-item__detail-value.is-amount {
  font-weight: 700;
}

.aw-list-popup__footer {
  display: flex;
  justify-content: flex-end;
}

.aw-list-popup__close {
  min-width: 78px;
  height: 40px;
  border: 0;
  border-radius: 999px;
  background-color: #d9d9d9;
  color: #232323;
  font-size: 14px;
  font-weight: 700;
}

@media (max-width: 359px) {
  .aw-list-popup {
    padding: 24px 18px 18px;
  }

  .aw-item__header-sub {
    flex-direction: column;
    gap: 4px;
  }
}
</style>
